import { FC, useCallback } from 'react';

import { CourseHearingListItem } from '@teachur/http-client';
import {
  SortableTable,
  SortableTableColumn,
  SortDirection,
} from '@new/common/ui/Table/SortableTable';
import { Button } from '@new/common/ui/Button/Button';
import { timestampToDate, formatShortDate } from '@new/common/lib/dateTime';
import { HearingStatusBadge } from '../HearingStatusBadge/HearingStatusBadge';
import { Loader } from '@new/common/ui/Loader/Loader';
import { useCourseHearingsContext, HearingSortKey } from '../../context/CourseHearingsContext';

function formatCallDate(startTime: number): string {
  const date = timestampToDate(startTime);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

const PAGE_SIZE = 10;

const isCallMissed = (hearing: CourseHearingListItem): boolean => {
  const attempt = hearing.latestAttempt;
  if (!attempt) return false;
  if (attempt.status !== 'accepted' && attempt.status !== 'pending') return false;
  if (!attempt.startTime) return false;
  const isPastCall = timestampToDate(attempt.startTime).getTime() < Date.now();
  const wasCallHeld = attempt.videoCallEndedAt;
  return isPastCall && !wasCallHeld;
};

export const CourseHearingsTable: FC = () => {
  const {
    calls,
    page,
    setPage,
    total,
    setSelectedCall,
    sortKey,
    sortDirection,
    setSort,
    isFetchingNewParams,
  } = useCourseHearingsContext();
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const onRowClick = (hearing: CourseHearingListItem) => {
    setSelectedCall(hearing);
  };

  const getRowClassName = useCallback((hearing: CourseHearingListItem): string => {
    if (isCallMissed(hearing)) return 'bg-[var(--danger-D200)]!';
    return '';
  }, []);

  const onSortChange = (key: string, direction: SortDirection) => {
    setSort(key as HearingSortKey, direction);
  };

  const columns: SortableTableColumn<CourseHearingListItem>[] = [
    {
      key: 'student',
      header: 'Student',
      className: 'max-w-[150px] truncate',
      render: (_: unknown, hearing: CourseHearingListItem) =>
        hearing.student ? `${hearing.student.firstName} ${hearing.student.lastName}` : '—',
    },
    {
      key: 'course',
      header: 'Course',
      className: 'max-w-[180px] truncate',
      render: (_: unknown, hearing: CourseHearingListItem) => hearing.course?.name ?? '—',
    },
    {
      key: 'instructor',
      header: 'Instructor',
      className: 'max-w-[150px] truncate',
      render: (_: unknown, hearing: CourseHearingListItem) => {
        const instructor = hearing.latestAttempt?.instructor;
        return instructor ? `${instructor.firstName} ${instructor.lastName}` : '—';
      },
    },
    {
      key: 'requestedAt',
      header: 'Requested time',
      sortable: true,
      className: 'max-w-[140px] truncate',
      render: (_: unknown, hearing: CourseHearingListItem) => {
        const requestedAt = hearing.latestAttempt?.createdAt ?? hearing.createdAt;
        return formatShortDate(requestedAt);
      },
    },
    {
      key: 'startTime',
      header: 'Meeting time',
      sortable: true,
      className: 'max-w-[160px] truncate',
      render: (_: unknown, hearing: CourseHearingListItem) => {
        const attempt = hearing.latestAttempt;
        return attempt ? formatCallDate(attempt.startTime) : '—';
      },
    },
    {
      key: 'derivedStatus',
      header: 'Status',
      className: 'max-w-[120px] truncate',
      render: (_: unknown, hearing: CourseHearingListItem) => (
        <HearingStatusBadge status={isCallMissed(hearing) ? 'missed' : hearing.derivedStatus} />
      ),
    },
  ];

  // While a changed query loads, keepPreviousData holds the prior response; the
  // context withholds it, so show a loader rather than stale rows or a
  // misleading "no results" state.
  if (isFetchingNewParams) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <SortableTable<CourseHearingListItem>
        data={calls}
        columns={columns}
        getRowKey={(hearing) => String(hearing.id)}
        onRowClick={onRowClick}
        getRowClassName={getRowClassName}
        sortKey={sortKey ?? undefined}
        sortDirection={sortDirection}
        onSortChange={onSortChange}
        emptyMessage="No course hearings found"
        emptyDescription="Try adjusting your search or filter criteria."
      />
      <div className="flex items-center gap-2 px-6 py-2 border-t border-[#e1e7ea]">
        <span className="inline-block w-3 h-3 rounded-sm bg-(--danger-D200)" />
        <span className="text-xs text-(--neutral-black-NB600)">Missed</span>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-3 border-t border-[#e1e7ea]">
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="neutral" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button
              variant="neutral"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
